import { Component, computed, inject, input, viewChild } from '@angular/core';

import { SiteWorkflowDirective } from '@features/feature-workflow/directives/site-workflow.directive';
import { NoteTextareaComponent } from '@features/note-textarea/note-textarea.component';

import {
  injectSaveReasonForAmendmentMutation,
  injectSiteBudget,
} from '@models/sites/api/site-budget.api';
import { injectSiteProtocolVersions } from '@models/sites/api/sites.api';

import {
  injectNgControl,
  NoopValueAccessorDirective,
} from '@shared/directives/noop-value-accessor.directive';
import { SkeletonLoaderDirective } from '@shared/directives/skeleton-loader.directive';
import { Maybe } from '@shared/utils/utils';

@Component({
  hostDirectives: [NoopValueAccessorDirective],
  imports: [NoteTextareaComponent, SkeletonLoaderDirective],
  selector: 'aux-site-budget-note',
  template: `
    <div auxSkeletonLoader [loading]="isProcessing()" class="h-full">
      <aux-note-textarea
        #noteTextarea
        [notes]="ngControl.value"
        (notesChange)="ngControl.control.setValue($event)"
        (saveNotes)="saveReasonForAmendment()"
        [disabled]="siteWorkflow.disabledUpdateSiteCostUI() || !protocolVersions.data()?.length"
        [disabledTooltip]="siteWorkflow.updateSiteCostTooltips()"
        placeholder="Write amendment here..."
      />
    </div>
  `,
})
export class SiteBudgetNoteComponent {
  siteWorkflow = inject(SiteWorkflowDirective);

  ngControl = injectNgControl();
  protocolVersions = injectSiteProtocolVersions();
  saveReasonForAmendmentMutation = injectSaveReasonForAmendmentMutation();

  selectedSiteId = input.required<Maybe<string>>();

  siteBudget = injectSiteBudget({ site_id: this.selectedSiteId });

  isProcessing = computed(() => {
    return this.saveReasonForAmendmentMutation.isPending() || this.siteBudget.isFetching();
  });

  noteId = input.required<null | string>();
  selectedSiteBudgetVersionId = input.required<null | string>();

  noteTextarea = viewChild.required(NoteTextareaComponent);

  cancelReasonForAmendment(previousNote: string) {
    this.ngControl.control.setValue(previousNote);
  }

  async saveReasonForAmendment() {
    if (!this.selectedSiteBudgetVersionId()) {
      return;
    }

    const initialValue = this.noteTextarea().initialValue;

    await this.saveReasonForAmendmentMutation.mutateAsync({
      note: {
        id: this.noteId() || null,
        message: this.ngControl.value,
      },
      previousNote: initialValue,
      site_id: this.selectedSiteId()!,
      siteBudgetVersionId: this.selectedSiteBudgetVersionId()!,
    });
  }
}
